/**
 * All display functions render in the user's local system timezone.
 * All toUTCISO / toDateOnlyISO functions produce strings safe to send to the API.
 */

const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

/**
 * Converts a local Date to a UTC ISO string (e.g. "2026-04-20T08:30:00.000Z").
 * Use this for all scheduled_start_at / scheduled_end_at fields.
 */
export const toUTCISO = (date: Date): string => date.toISOString();

/**
 * Returns a date-only string in "YYYY-MM-DD" format using the local calendar
 * date.  Avoids the day-shift that .toISOString() causes for UTC+ timezones
 * when the local time is before midnight UTC (e.g. IST, SGT).
 * Use this for date_of_birth and similar date-only API fields.
 */
export const toDateOnlyISO = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/**
 * Approximates a date of birth from a whole-number age in years, using
 * today's month/day. Used when a patient's exact date of birth isn't known
 * but their age is.
 */
export const getDateOfBirthFromAge = (age: number): Date => {
  const today = new Date();
  return new Date(
    today.getFullYear() - age,
    today.getMonth(),
    today.getDate(),
  );
};

export const isSameLocalDate = (date: Date, compareTo = new Date()): boolean =>
  date.getFullYear() === compareTo.getFullYear() &&
  date.getMonth() === compareTo.getMonth() &&
  date.getDate() === compareTo.getDate();

export const isBeforeLocalToday = (date: Date, compareTo = new Date()): boolean => {
  const today = new Date(compareTo);
  today.setHours(0, 0, 0, 0);
  return date < today;
};

export const isPastOrCurrentDateTime = (
  date: Date,
  compareTo = new Date(),
): boolean => date.getTime() <= compareTo.getTime();

export const formatTimeInputValue = (date: Date): string =>
  `${date.getHours().toString().padStart(2, "0")}:${date
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;

export const getMinimumFutureTimeInputValue = (date = new Date()): string => {
  const nextMinute = new Date(date);
  nextMinute.setMinutes(nextMinute.getMinutes() + 1, 0, 0);
  return formatTimeInputValue(nextMinute);
};

export const getAllowedFutureTimeInputValue = (
  selectedDate: Date | null | undefined,
  time: string,
  compareTo = new Date(),
): string => {
  if (!selectedDate || !time || !isSameLocalDate(selectedDate, compareTo)) {
    return time;
  }

  const minimumTime = getMinimumFutureTimeInputValue(compareTo);
  return time < minimumTime ? minimumTime : time;
};

// ---------------------------------------------------------------------------
// Display helpers  (all output in local timezone)
// ---------------------------------------------------------------------------

/**
 * "15 Apr, 2:30 pm"  — used wherever a compact datetime is shown in the UI.
 */
export const formatDateTimeShort = (iso?: string | null): string => {
  if (!iso) return "—";

  const date = parseISOToLocal(iso);
  if (!date || isNaN(date.getTime())) return "—";

  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });

  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");

  const period = hours >= 12 ? "pm" : "am";
  const h = hours % 12 || 12;

  return `${day} ${month}, ${h}:${minutes} ${period}`;
};
/**
 * "15 Apr 2026"  — date only, in local timezone.
 */
export const formatDateShort = (iso?: string | null): string => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: LOCAL_TZ,
  });
};

const parseISOToLocal = (iso: string) => {
  if (!iso) return null;

  // ✅ If no timezone, assume UTC
  if (!iso.endsWith("Z")) {
    return new Date(iso + "Z");
  }

  return new Date(iso);
};

/**
 * Relative time: "Just now", "5 mins ago", "Yesterday", "3 days ago", etc.
 * Falls back to formatDateShort for dates older than 7 days.
 */
export const formatRelativeTime = (iso?: string | null): string => {
  if (!iso) return "—";

  const date = parseISOToLocal(iso);
  if (!date || isNaN(date.getTime())) return "—";

  let diffMs = Date.now() - date.getTime();
  if (diffMs < 0) diffMs = 0;

  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} min${diffMin > 1 ? "s" : ""} ago`;
  if (diffHr < 24) return `${diffHr} hr${diffHr > 1 ? "s" : ""} ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;

  return formatDateShort(iso);
};

export const formatDateTimeLocal = (iso: string): string => {
  const date = parseISOToLocal(iso);

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
    .format(date)
    .replace(",", "");
};

export const hasAppointmentStarted = (
  iso?: string | null,
  compareTo = Date.now(),
): boolean => {
  if (!iso) return true;

  const date = parseISOToLocal(iso);
  if (!date || isNaN(date.getTime())) return true;

  return compareTo >= date.getTime();
};

export const isAppointmentInProgress = (
  startIso?: string | null,
  endIso?: string | null,
  compareTo = Date.now(),
): boolean => {
  if (!startIso || !endIso) return false;

  const start = parseISOToLocal(startIso);
  const end = parseISOToLocal(endIso);
  if (
    !start ||
    !end ||
    isNaN(start.getTime()) ||
    isNaN(end.getTime())
  ) {
    return false;
  }

  return compareTo >= start.getTime() && compareTo <= end.getTime();
};

export const formatTimeUntilStart = (
  iso?: string | null,
  compareTo = Date.now(),
): string | null => {
  if (!iso) return null;

  const date = parseISOToLocal(iso);
  if (!date || isNaN(date.getTime())) return null;

  const diffMs = date.getTime() - compareTo;
  if (diffMs <= 0) return null;

  const diffMin = Math.ceil(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffDays > 0) {
    const hrs = diffHr % 24;
    const dayPart = `${diffDays} day${diffDays > 1 ? "s" : ""}`;
    return hrs > 0
      ? `${dayPart} ${hrs} hr${hrs > 1 ? "s" : ""}`
      : dayPart;
  }

  if (diffHr > 0) {
    const mins = diffMin % 60;
    const hrPart = `${diffHr} hr${diffHr > 1 ? "s" : ""}`;
    return mins > 0
      ? `${hrPart} ${mins} min${mins > 1 ? "s" : ""}`
      : hrPart;
  }

  return `${diffMin} min${diffMin !== 1 ? "s" : ""}`;
};

export const formatRelativeTimeWithDays = (iso?: string | null) => {
  if (!iso) return { absolute: "—", relative: "" };

  const date = parseISOToLocal(iso);
  if (!date || isNaN(date.getTime())) return { absolute: "—", relative: "" };

  let diffMs = Date.now() - date.getTime();
  if (diffMs < 0) diffMs = 0;

  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  let relative = "";

  if (diffMin < 1) relative = "Just now";
  else if (diffMin < 60)
    relative = `${diffMin} min${diffMin > 1 ? "s" : ""} ago`;
  else if (diffHr < 24) relative = `${diffHr} hr${diffHr > 1 ? "s" : ""} ago`;
  else if (diffDays === 1) relative = "Yesterday";
  else if (diffDays < 7) relative = `${diffDays} days ago`;
  else relative = formatDateShort(iso);

  const absolute = formatDateTimeLocal(iso);

  return { absolute, relative };
};
